import { useEffect, useState, useCallback } from "react";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  addDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import { GAME_ID, randomId, formatMoney } from "../utils/helpers";

// Función helper para agregar logs al historial
const addLog = async (type, message, data = {}) => {
  try {
    const historialCol = collection(db, "games", GAME_ID, "historial");
    await addDoc(historialCol, {
      type,
      message,
      data,
      timestamp: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al agregar log:", error);
  }
};

export function useGameLogic() {
  const [playerId, setPlayerId] = useState(null);
  const [playerName, setPlayerName] = useState("");
  const [playerData, setPlayerData] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [topBids, setTopBids] = useState([]);
  const [bidAmount, setBidAmount] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [pollVote, setPollVote] = useState(null);
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1000);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [totalVotes, setTotalVotes] = useState(0);
  const [isProcessingRound, setIsProcessingRound] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [isSpectator, setIsSpectator] = useState(false);
  const [isShowingResume, setIsShowingResume] = useState(false);

  // Cargar playerId desde localStorage y verificar si existe
  useEffect(() => {
    const checkStoredPlayer = async () => {
      const stored = localStorage.getItem("panGamePlayerId");
      if (stored) {
        // Verificar si el jugador todavía existe en Firebase
        const playerRef = doc(db, "games", GAME_ID, "players", stored);
        const snap = await getDoc(playerRef);
        
        if (snap.exists()) {
          // El jugador existe, usar ese ID y cargar sus datos
          setPlayerId(stored);
          const data = snap.data();
          if (data.name) {
            setPlayerName(data.name);
          }
        } else {
          // El jugador fue eliminado (juego reseteado), limpiar localStorage
          console.log("Jugador eliminado de Firebase, limpiando localStorage");
          localStorage.removeItem("panGamePlayerId");
          setPlayerId(null);
        }
      }
    };
    
    checkStoredPlayer();
  }, []);

  // Manejar el ingreso del jugador
  const handleJoin = async () => {
    if (!playerName.trim()) {
      alert("Pon tu nombre");
      return;
    }
    
    try {
      // Verificar si el juego ya comenzó
      const gameRef = doc(db, "games", GAME_ID);
      const gameSnap = await getDoc(gameRef);
      
      if (gameSnap.exists()) {
        const data = gameSnap.data();
        if (data.gameStarted === true) {
          // Modo espectador: no crear jugador, solo observar
          setIsSpectator(true);
          setPlayerId("spectator");
          console.log("Modo espectador activado - juego ya comenzó");
          return;
        }
      }
      
      // Usar el nombre (normalizado) como ID del documento para evitar duplicados
      // Convertir a minúsculas y remover espacios para consistencia
      const normalizedName = playerName.trim().toLowerCase().replace(/\s+/g, '_');
      const pid = normalizedName;
      
      const playerRef = doc(db, "games", GAME_ID, "players", pid);
      const snap = await getDoc(playerRef);
      
      if (!snap.exists()) {
        // Crear nuevo jugador
        await setDoc(playerRef, {
          name: playerName.trim(),
          money: 5,
          breads: 0,
          active: true,
        });
        console.log("Jugador guardado en Firebase:", playerName.trim());
        await addLog("player_join", `${playerName.trim()} se ha unido al juego`, {
          playerId: pid,
          playerName: playerName.trim(),
        });
      } else {
        // Actualizar jugador existente (reconexión)
        await updateDoc(playerRef, { 
          name: playerName.trim(),
          active: true,
        });
        console.log("Jugador actualizado en Firebase:", playerName.trim());
        await addLog("player_rejoin", `${playerName.trim()} ha vuelto al juego`, {
          playerId: pid,
          playerName: playerName.trim(),
        });
      }
      
      // Guardar en localStorage y establecer en estado
      localStorage.setItem("panGamePlayerId", pid);
      setPlayerId(pid);
      console.log("PlayerId establecido:", pid);
      
    } catch (error) {
      console.error("Error al unirse al juego:", error);
      alert("Error al unirse al juego. Intenta de nuevo.");
    }
  };

  // Suscripción al jugador
  useEffect(() => {
    if (!playerId) return;
    if (playerId === "spectator") return; // Los espectadores no tienen jugador en Firebase
    
    const playerRef = doc(db, "games", GAME_ID, "players", playerId);
    const unsub = onSnapshot(playerRef, (snap) => {
      if (!snap.exists()) {
        // El jugador fue eliminado (juego reseteado), limpiar y volver a login
        console.log("Jugador eliminado, regresando a login");
        localStorage.removeItem("panGamePlayerId");
        setPlayerId(null);
        setPlayerData(null);
        setPlayerName("");
        return;
      }
      
      const data = snap.data();
      
      // Si el jugador fue marcado como inactivo (eliminado del juego)
      if (data.active === false) {
        console.log("Jugador marcado como inactivo (eliminado del juego)");
        setPlayerData({ id: snap.id, ...data });
        // No limpiar playerId para que pueda ver el lobby como eliminado
        return;
      }
      
      setPlayerData({ id: snap.id, ...data });
      if (!playerName) {
        setPlayerName(data.name || "");
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  // Suscripción al juego
  useEffect(() => {
    const gameRef = doc(db, "games", GAME_ID);
    let lastReloadTimestamp = null;
    
    const unsub = onSnapshot(gameRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      
      // Detectar señal de recarga forzada
      if (data.forceReload && data.forceReload !== lastReloadTimestamp) {
        console.log("Señal de recarga detectada, refrescando navegador...");
        lastReloadTimestamp = data.forceReload;
        window.location.reload();
        return;
      }
      
      setGameData(data);
    });
    return () => unsub();
  }, []);

  // Resetear bidAmount cuando cambia de ronda
  useEffect(() => {
    if (gameData) {
      setBidAmount("");
      setPollVote(null); // Resetear voto de encuesta
    }
  }, [gameData]);

  // Suscripción para contar jugadores totales y obtener lista
  useEffect(() => {
    const playersCol = collection(db, "games", GAME_ID, "players");
    const unsub = onSnapshot(playersCol, (snap) => {
      setTotalPlayers(snap.size);
      const playersList = [];
      snap.forEach((doc) => {
        playersList.push({ id: doc.id, ...doc.data() });
      });
      // Ordenar alfabéticamente por nombre
      playersList.sort((a, b) => a.name.localeCompare(b.name));
      setAllPlayers(playersList);
    });
    return () => unsub();
  }, []);

  // Suscripción para contar votos de la ronda actual
  useEffect(() => {
    if (!gameData) return;
    const currentRound = gameData.currentRound || 1;
    const votesCol = collection(db, "games", GAME_ID, "rounds", String(currentRound), "pollVotes");
    const unsub = onSnapshot(votesCol, (snap) => {
      setTotalVotes(snap.size);
    });
    return () => unsub();
  }, [gameData]);

  // Suscripción al Top 5 de pujas
  useEffect(() => {
    if (!gameData) return;
    const currentRound = gameData.currentRound || 1;
    const bidsCol = collection(db, "games", GAME_ID, "rounds", String(currentRound), "bids");
    // Ordenar por cantidad descendente, y en caso de empate, por tiempo ascendente (el primero gana)
    const qTop = query(bidsCol, orderBy("amount", "desc"), orderBy("updatedAt", "asc"), limit(5));
    const unsub = onSnapshot(qTop, (snap) => {
      const arr = [];
      snap.forEach((d) => arr.push(d.data()));
      setTopBids(arr);
    });
    return () => unsub();
  }, [gameData]);

  // Procesar votos de la encuesta y actualizar stipendMultiplier
  const processPollVotes = useCallback(async (currentRound) => {
    try {
      const gameRef = doc(db, "games", GAME_ID);
      
      // Usar transacción para verificar y marcar como procesada de forma atómica
      const shouldProcess = await runTransaction(db, async (transaction) => {
        const gameSnap = await transaction.get(gameRef);
        const gameState = gameSnap.data();
        
        // Verificar si la encuesta ya fue procesada en esta ronda
        if (gameState.lastProcessedPoll === currentRound) {
          console.log(`Encuesta de ronda ${currentRound} ya fue procesada, saltando...`);
          return false;
        }
        
        // Marcar como procesada de forma atómica
        transaction.update(gameRef, {
          lastProcessedPoll: currentRound,
        });
        
        return true;
      });
      
      if (!shouldProcess) {
        return;
      }
      
      console.log(`Procesando votos de ronda ${currentRound}...`);
      
      // Obtener el estado del juego (reutilizando gameRef existente)
      const gameSnap = await getDoc(gameRef);
      const gameState = gameSnap.data();
      
      const votesCol = collection(db, "games", GAME_ID, "rounds", String(currentRound), "pollVotes");
      const votesSnap = await getDocs(votesCol);
      
      if (votesSnap.empty) {
        console.log("No hay votos en esta ronda");
        return;
      }

      // Contar votos
      let increaseVotes = 0;
      let decreaseVotes = 0;
      
      votesSnap.forEach((doc) => {
        const vote = doc.data().vote;
        if (vote === "increase") increaseVotes++;
        else if (vote === "decrease") decreaseVotes++;
      });
      
      console.log(`Votos: Aumentar=${increaseVotes}, Reducir=${decreaseVotes}`);
      
      // Obtener el multiplicador actual
      const currentMultiplier = gameState.stipendMultiplier || 1;
      
      // Determinar el resultado
      let newMultiplier;
      let result;
      
      if (increaseVotes > decreaseVotes) {
        // Mayoría quiere aumentar: multiplicador acumulativo ×1.5
        newMultiplier = currentMultiplier * 1.5;
        result = "increase";
      } else if (decreaseVotes > increaseVotes) {
        // Mayoría quiere reducir: multiplicador acumulativo ×0.5
        newMultiplier = currentMultiplier * 0.5;
        result = "decrease";
      } else {
        // Empate: multiplicador acumulativo ×1.5
        newMultiplier = currentMultiplier * 1.5;
        result = "tie";
      }
      
      await updateDoc(gameRef, {
        stipendMultiplier: newMultiplier,
      });
      
      await addLog("poll_processed", `Encuesta procesada en ronda ${currentRound}`, {
        round: currentRound,
        increaseVotes,
        decreaseVotes,
        result,
        previousMultiplier: currentMultiplier,
        newMultiplier,
      });
      
      console.log(`Resultado de la encuesta: ${result}, nuevo multiplicador: ${newMultiplier}`);
    } catch (error) {
      console.error("Error al procesar votos:", error);
      await addLog("poll_error", `Error al procesar votos en ronda ${currentRound}`, {
        error: error.message,
      });
    }
  }, []);

  // Función para procesar la subasta al final de la ronda
  // Distribuir ayuda a los jugadores con menos dinero
  const distributeStipend = useCallback(async (currentRound, baseStipend, stipendMultiplier) => {
    try {
      const gameRef = doc(db, "games", GAME_ID);
      
      // Usar transacción para verificar y marcar como distribuida de forma atómica
      const shouldProcess = await runTransaction(db, async (transaction) => {
        const gameSnap = await transaction.get(gameRef);
        const gameState = gameSnap.data();
        
        if (gameState.lastStipendRound === currentRound) {
          console.log(`Ayuda de ronda ${currentRound} ya fue distribuida, saltando...`);
          return false;
        }
        
        // Marcar como distribuida de forma atómica
        transaction.update(gameRef, {
          lastStipendRound: currentRound,
        });
        
        return true;
      });
      
      if (!shouldProcess) {
        return;
      }
      
      console.log(`Distribuyendo ayuda en ronda ${currentRound}...`);
      
      const playersCol = collection(db, "games", GAME_ID, "players");
      
      // Re-obtener los datos frescos de Firebase para asegurar que tenemos
      // el estado más reciente DESPUÉS de las eliminaciones
      const playersSnap = await getDocs(playersCol);
      
      if (playersSnap.empty) {
        console.log("No hay jugadores para distribuir ayuda");
        return;
      }

      // Obtener solo jugadores ACTIVOS (sobrevivientes de la ronda) 
      // y ordenarlos por dinero (menor a mayor)
      const activePlayers = [];
      playersSnap.forEach((doc) => {
        const data = doc.data();
        // Solo incluir jugadores activos (no eliminados) para la distribución de ayuda
        if (data.active !== false) {
          activePlayers.push({ id: doc.id, ...data });
        }
      });
      
      if (activePlayers.length === 0) {
        console.log("No hay jugadores activos para distribuir ayuda");
        return;
      }
      
      activePlayers.sort((a, b) => (a.money || 0) - (b.money || 0));
      
      console.log(`Distribuyendo beca a jugadores activos en ronda ${currentRound}:`, 
        activePlayers.map(p => `${p.name} ($${p.money || 0})`).join(", "));
      
      // Calcular cuántos jugadores recibirán ayuda (mitad redondeada hacia abajo)
      const numRecipients = Math.floor(activePlayers.length / 2);
      
      if (numRecipients === 0) {
        console.log("No hay suficientes jugadores para distribuir ayuda");
        return;
      }

      // Calcular el monto de ayuda (redondeado hacia abajo)
      const stipendAmount = Math.floor(baseStipend * (stipendMultiplier || 1));
      
      // Distribuir ayuda a los jugadores sobrevivientes con menos dinero
      const recipients = activePlayers.slice(0, numRecipients);
      
      console.log(`Dando ${formatMoney(stipendAmount)} a ${recipients.length} jugadores:`, 
        recipients.map(p => p.name).join(", "));
      
      for (const player of recipients) {
        const playerRef = doc(db, "games", GAME_ID, "players", player.id);
        const currentMoney = player.money || 0;
        
        console.log(`💰 Dando beca a ${player.name}: tenía $${currentMoney}, recibirá $${stipendAmount}, total: $${currentMoney + stipendAmount}`);
        
        await updateDoc(playerRef, {
          money: currentMoney + stipendAmount,
        });
        
        await addLog("stipend_distributed", `${player.name} recibió ayuda estatal`, {
          round: currentRound,
          playerId: player.id,
          playerName: player.name,
          amount: stipendAmount,
          moneyBefore: currentMoney,
          moneyAfter: currentMoney + stipendAmount,
        });
      }
      
      console.log(`✅ Ayuda distribuida a ${numRecipients} jugadores sobrevivientes: ${formatMoney(stipendAmount)} cada uno`);
      
      await addLog("stipend_summary", `Ayuda distribuida en ronda ${currentRound}`, {
        round: currentRound,
        recipients: numRecipients,
        totalActivePlayers: activePlayers.length,
        amount: stipendAmount,
        totalDistributed: stipendAmount * numRecipients,
      });
    } catch (error) {
      console.error("Error al distribuir ayuda:", error);
      await addLog("stipend_error", `Error al distribuir ayuda en ronda ${currentRound}`, {
        error: error.message,
      });
    }
  }, []);

  const processAuction = useCallback(async (currentRound) => {
    try {
      const gameRef = doc(db, "games", GAME_ID);
      
      // Usar transacción para verificar y marcar como procesada de forma atómica
      const shouldProcess = await runTransaction(db, async (transaction) => {
        const gameSnap = await transaction.get(gameRef);
        const gameState = gameSnap.data();
        
        // Verificar si ya se procesó
        if (gameState.lastProcessedRound === currentRound) {
          console.log(`Ronda ${currentRound} ya fue procesada, saltando...`);
          return false; // No procesar
        }
        
        // Marcar como procesada INMEDIATAMENTE de forma atómica
        transaction.update(gameRef, {
          lastProcessedRound: currentRound,
        });
        
        return true; // Sí procesar
      });
      
      // Si ya fue procesada por otro cliente, salir
      if (!shouldProcess) {
        return;
      }
      
      console.log(`Procesando subasta de ronda ${currentRound}...`);
      
      // Obtener estado del juego
      const gameSnap = await getDoc(gameRef);
      const gameState = gameSnap.data();
      
      // Obtener TODAS las ofertas de la ronda actual
      const bidsCol = collection(db, "games", GAME_ID, "rounds", String(currentRound), "bids");
      const qBids = query(bidsCol, orderBy("amount", "desc"), orderBy("updatedAt", "asc"));
      const bidsSnap = await getDocs(qBids);
      
      if (bidsSnap.empty) {
        console.log("No hay ofertas en esta ronda");
        await addLog("auction_no_bids", `Ronda ${currentRound}: No hubo ofertas`, {
          round: currentRound,
        });
        // Marcar como procesada incluso si no hay ofertas
        await updateDoc(gameRef, {
          lastProcessedRound: currentRound,
        });
        return;
      }

      // Obtener al ganador (primera oferta más alta) para mostrar en pantalla
      const winnerBid = bidsSnap.docs[0].data();
      const winAmount = winnerBid.amount;

      console.log(`Ganador de la ronda ${currentRound}:`, winnerBid.playerName, "con oferta de", winAmount);
      console.log(`Total de jugadores que pujaron: ${bidsSnap.size}`);

      // Obtener el número total de jugadores activos para calcular cuántos panes dar
      const playersCol = collection(db, "games", GAME_ID, "players");
      const playersSnap = await getDocs(playersCol);
      const activePlayers = [];
      playersSnap.forEach((doc) => {
        const data = doc.data();
        if (data.active !== false) {
          activePlayers.push({ id: doc.id, ...data });
        }
      });
      const totalActivePlayers = activePlayers.length;

      // Calcular cuántos panes dar según el número de jugadores activos:
      // - 3 jugadores: 1 pan (ronda final, único ganador)
      // - 4-5 jugadores: N-1 panes (eliminar 1 por ronda)
      // - Más de 15 jugadores: N-4 panes (eliminar 4 por ronda)
      // - De 6 a 15 jugadores: N-3 panes (eliminar 3 por ronda)
      let panesADar;
      if (totalActivePlayers <= 3) {
        panesADar = 1; // Solo 1 ganador cuando quedan 3 o menos (ronda final)
      } else if (totalActivePlayers <= 5) {
        panesADar = totalActivePlayers - 1; // Eliminar 1 cuando quedan 4-5 jugadores
      } else if (totalActivePlayers > 15) {
        panesADar = totalActivePlayers - 4; // Eliminar 4 cuando hay más de 15
      } else {
        panesADar = totalActivePlayers - 3; // Eliminar 3 cuando hay de 6 a 15
      }
      
      const isFinalRound = totalActivePlayers <= 3;
      console.log(`Ronda ${currentRound}: ${totalActivePlayers} jugadores activos, se darán ${panesADar} pan(es) (Eliminar: ${totalActivePlayers - panesADar})`);

      // Dar pan solo a los primeros N-1 (o todos en rondas posteriores)
      const winnersInfo = [];
      const winnerIds = []; // Guardar IDs de los ganadores para la ronda final
      let panesAsignados = 0;
      
      for (const bidDoc of bidsSnap.docs) {
        if (panesAsignados >= panesADar) {
          break; // Ya se asignaron todos los panes disponibles
        }
        
        const bid = bidDoc.data();
        const playerRef = doc(db, "games", GAME_ID, "players", bid.playerId);
        const playerSnap = await getDoc(playerRef);
        
        if (playerSnap.exists()) {
          const playerData = playerSnap.data();
          const currentMoney = playerData.money || 0;
          const currentBreads = playerData.breads || 0;

          // Verificar que el jugador tenga suficiente dinero
          const actualDeduction = Math.min(bid.amount, currentMoney);
          const finalMoney = Math.max(0, currentMoney - bid.amount);

          await updateDoc(playerRef, {
            money: finalMoney,
            breads: currentBreads + 1, // Gana 1 pan
          });

          winnersInfo.push({
            name: bid.playerName,
            amount: bid.amount,
          });
          winnerIds.push(bid.playerId); // Guardar ID del ganador

          await addLog("auction_winner", `${bid.playerName} compró pan en la ronda ${currentRound}`, {
            round: currentRound,
            playerId: bid.playerId,
            playerName: bid.playerName,
            bidAmount: bid.amount,
            actualDeduction: actualDeduction,
            moneyBefore: currentMoney,
            moneyAfter: finalMoney,
            breadsBefore: currentBreads,
            breadsAfter: currentBreads + 1,
          });

          console.log(`Actualizado jugador ${bid.playerName}: -${actualDeduction} dinero, +1 pan`);
          panesAsignados++;
        }
      }

      // Guardar información del ganador principal (para mostrar en pantalla)
      await updateDoc(gameRef, {
        lastRoundWinner: {
          name: winnerBid.playerName,
          amount: winAmount,
          round: currentRound,
          totalWinners: panesAsignados, // Cuántos jugadores ganaron pan
        },
      });

      // CONSUMIR PAN: Todos los jugadores activos consumen 1 pan para sobrevivir esta ronda
      // Esto debe hacerse ANTES de eliminar jugadores
      console.log("Consumiendo pan de todos los jugadores...");
      for (const playerDoc of playersSnap.docs) {
        const playerData = playerDoc.data();
        if (playerData.active !== false) {
          const currentBreads = playerData.breads || 0;
          const newBreads = Math.max(0, currentBreads - 1);
          await updateDoc(playerDoc.ref, {
            breads: newBreads,
          });
          console.log(`${playerData.name}: ${currentBreads} pan(es) → ${newBreads} pan(es) después de consumir`);
        }
      }

      // ELIMINAR jugadores sin pan (active: false)
      // Re-obtener los datos de los jugadores para tener valores actualizados después del consumo
      const updatedPlayersSnap = await getDocs(playersCol);
      
      for (const playerDoc of updatedPlayersSnap.docs) {
        const playerData = playerDoc.data();
        const breads = playerData.breads || 0;
        
        // En la ronda final, eliminar a todos los que NO ganaron pan en esta subasta
        if (isFinalRound) {
          const wonThisRound = winnerIds.includes(playerDoc.id);
          if (!wonThisRound && playerData.active !== false) {
            // Jugador eliminado en la ronda final
            await updateDoc(playerDoc.ref, {
              active: false,
            });
            
            await addLog("player_eliminated", `${playerData.name} fue eliminado en la ronda final`, {
              round: currentRound,
              playerId: playerDoc.id,
              playerName: playerData.name,
              money: playerData.money || 0,
            });
            
            console.log(`Jugador ${playerData.name} eliminado en la ronda final (no ganó la subasta)`);
          }
        } else if (breads === 0 && playerData.active !== false) {
          // En rondas normales, eliminar si se quedó sin pan después de consumir
          await updateDoc(playerDoc.ref, {
            active: false,
          });
          
          await addLog("player_eliminated", `${playerData.name} fue eliminado por no tener pan`, {
            round: currentRound,
            playerId: playerDoc.id,
            playerName: playerData.name,
            money: playerData.money || 0,
          });
          
          console.log(`Jugador ${playerData.name} eliminado (sin pan después de consumir)`);
        }
      }
    } catch (error) {
      console.error("Error al procesar la subasta:", error);
      await addLog("auction_error", `Error al procesar subasta de ronda ${currentRound}`, {
        error: error.message,
      });
    }
  }, []);

  // Calcular tiempo restante y manejar inicio/fin de ronda
  useEffect(() => {
    if (!gameData) {
      setTimeLeft(null);
      return;
    }

    // Si el juego está pausado, mantener el tiempo actual sin actualizarlo
    if (gameData.isPaused) {
      // No crear ningún interval, solo mantener el timeLeft actual
      return;
    }

    let hasProcessedEnd = false; // Bandera para evitar ejecución múltiple

    // Si la ronda está abierta, usar roundEndTime (10 minutos de juego)
    if (gameData.roundStatus === "open" && gameData.roundEndTime) {
      const endMs = gameData.roundEndTime.toMillis
        ? gameData.roundEndTime.toMillis()
        : gameData.roundEndTime;
      
      const interval = setInterval(async () => {
        // Verificar nuevamente si está pausado dentro del interval
        if (gameData.isPaused) {
          return;
        }
        
        const diff = Math.max(0, Math.round((endMs - Date.now()) / 1000));
        setTimeLeft(diff);
        
        // Cuando llegue a 0, finalizar la ronda y pasar a la siguiente
        if (diff === 0 && gameData.roundStatus === "open" && !hasProcessedEnd) {
          hasProcessedEnd = true; // Marcar como procesado
          setIsProcessingRound(true); // Mostrar pantalla de loading
          try {
            const currentRound = gameData.currentRound || 1;
            
            // Procesar la subasta primero
            await processAuction(currentRound);
            
            // Procesar votos de la encuesta (si es ronda 2-5)
            if (currentRound >= 2 && currentRound <= 5) {
              await processPollVotes(currentRound);
            }
            
            // Esperar un momento para asegurar que Firebase esté actualizado
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Distribuir ayuda a los jugadores con menos dinero (excepto en la última ronda)
            // Contar jugadores activos para decidir si distribuir beca
            const playersColBeca = collection(db, "games", GAME_ID, "players");
            const playersSnapBeca = await getDocs(playersColBeca);
            let activePlayersCountBeca = 0;
            playersSnapBeca.forEach((doc) => {
              const data = doc.data();
              if (data.active !== false) {
                activePlayersCountBeca++;
              }
            });
            const isNotFinalRound = activePlayersCountBeca > 3;
            console.log(`Evaluando distribución de beca: ${activePlayersCountBeca} jugadores activos, isNotFinalRound=${isNotFinalRound}`);
            if (isNotFinalRound) {
              console.log("Distribuyendo beca...");
              // Obtener el multiplicador actualizado
              const gameSnap = await getDoc(doc(db, "games", GAME_ID));
              const updatedGameData = gameSnap.data();
              const baseStipend = updatedGameData.baseStipend || 10;
              const stipendMultiplier = updatedGameData.stipendMultiplier || 1;
              await distributeStipend(currentRound, baseStipend, stipendMultiplier);
            } else {
              console.log("NO se distribuye beca (ronda final)");
            }
            
            const gameRef = doc(db, "games", GAME_ID);
            
            // Contar jugadores activos para decidir si terminar el juego
            const playersColEnd = collection(db, "games", GAME_ID, "players");
            const playersSnapEnd = await getDocs(playersColEnd);
            let activePlayersCountEnd = 0;
            playersSnapEnd.forEach((doc) => {
              const data = doc.data();
              if (data.active !== false) {
                activePlayersCountEnd++;
              }
            });
            
            if (activePlayersCountEnd <= 3) {
              // Juego terminado - quedan 3 o menos jugadores
              await updateDoc(gameRef, {
                roundStatus: "finished",
              });
              await addLog("game_finished", `Juego terminado después de ${currentRound} rondas con ${activePlayersCountEnd} jugador(es) restante(s)`, {
                finalRound: currentRound,
                remainingPlayers: activePlayersCountEnd,
              });
              console.log(`Juego terminado después de ronda ${currentRound} - ${activePlayersCountEnd} jugador(es) restante(s)`);
            } else {
              // Pasar a la siguiente ronda
              const nextRound = currentRound + 1;
              const resumeTime = 30000; // 30 segundos para mostrar resumen
              const waitTime = resumeTime; // Tiempo de espera antes de iniciar la siguiente ronda
              const nextGameStart = new Date(Date.now() + waitTime);
              
              await updateDoc(gameRef, {
                currentRound: nextRound,
                roundStatus: "resume", // Nuevo estado para mostrar resumen
                gameStart: nextGameStart,
              });
              await addLog("round_ended", `Ronda ${currentRound} finalizada. Mostrando resumen durante 30s. Siguiente ronda: ${nextRound}`, {
                roundEnded: currentRound,
                nextRound: nextRound,
                waitTime: waitTime / 1000,
              });
              console.log("Mostrando resumen de ronda", currentRound, "antes de pasar a ronda", nextRound);
            }
            
            // Esperar 2 segundos antes de continuar para dar tiempo a que se actualice la UI
            setTimeout(() => {
              setIsProcessingRound(false);
            }, 2000);
          } catch (error) {
            console.error("Error al finalizar la ronda:", error);
            setIsProcessingRound(false);
          }
        }
      }, 1000);
      return () => clearInterval(interval);
    }

    // Si no está abierta, usar gameStart (cuenta regresiva hasta inicio)
    if ((gameData.roundStatus === "waiting" || gameData.roundStatus === "resume") && gameData.gameStart) {
      // Si está en estado "resume", activar el flag para mostrar la página de resumen
      if (gameData.roundStatus === "resume") {
        setIsShowingResume(true);
      } else {
        setIsShowingResume(false);
      }
      
      const startMs = gameData.gameStart.toMillis
        ? gameData.gameStart.toMillis()
        : gameData.gameStart;
      
      const interval = setInterval(async () => {
        // Verificar si está pausado dentro del interval
        if (gameData.isPaused) {
          return;
        }
        
        const diff = Math.max(0, Math.round((startMs - Date.now()) / 1000));
        setTimeLeft(diff);
        
        // Cuando llegue a 0, iniciar la ronda
        if (diff === 0 && (gameData.roundStatus === "waiting" || gameData.roundStatus === "resume") && !hasProcessedEnd) {
          hasProcessedEnd = true; // Marcar como procesado
          setIsShowingResume(false); // Ocultar resumen
          try {
            const currentRound = gameData.currentRound || 1;
            const gameRef = doc(db, "games", GAME_ID);
            const roundDuration = 5 * 60 * 1000; // 5 minutos en ms
            const roundEndTime = new Date(Date.now() + roundDuration);
            
            // Contar jugadores activos para decidir si es la última ronda
            const playersCol = collection(db, "games", GAME_ID, "players");
            const playersSnap = await getDocs(playersCol);
            const activePlayers = [];
            playersSnap.forEach((doc) => {
              const data = doc.data();
              if (data.active !== false) {
                activePlayers.push(doc.id);
              }
            });
            const numPlayers = activePlayers.length;
            
            // Sin límite de rondas: el juego continúa hasta que queden 5 o menos jugadores
            console.log(`Iniciando ronda ${currentRound} con ${numPlayers} jugadores activos`);
            
            const updateData = {
              roundStatus: "open",
              roundEndTime: roundEndTime,
              gameStarted: true, // Marcar que el juego ya comenzó
            };
            
            await updateDoc(gameRef, updateData);
            await addLog("round_started", `Ronda ${currentRound} iniciada`, {
              round: currentRound,
              duration: roundDuration / 1000,
              totalPlayers: numPlayers,
            });
            console.log("Ronda iniciada");
          } catch (error) {
            console.error("Error al iniciar la ronda:", error);
          }
        }
      }, 1000);
      return () => clearInterval(interval);
    }

    setTimeLeft(null);
  }, [gameData, processAuction, processPollVotes, distributeStipend]);

  // Otorgar dinero por mini-juegos
  const awardMoney = useCallback(
    async (amount, source = "minigame") => {
      if (!playerData) return;
      const playerRef = doc(db, "games", GAME_ID, "players", playerData.id);
      const snap = await getDoc(playerRef);
      if (!snap.exists()) return;
      const data = snap.data();
      const currentMoney = data.money || 0;
      await updateDoc(playerRef, {
        money: currentMoney + amount,
      });
      await addLog("minigame_reward", `${playerData.name} ganó dinero en minijuego`, {
        playerId: playerData.id,
        playerName: playerData.name,
        amount: amount,
        source: source,
        moneyBefore: currentMoney,
        moneyAfter: currentMoney + amount,
        round: gameData?.currentRound,
      });
      setStatusMsg(`Ganaste ${formatMoney(amount)} por ${source}.`);
    },
    [playerData, gameData]
  );

  // Ofertar por pan
  const handleBid = async () => {
    setStatusMsg("");
    if (!playerData || !gameData) return;
    if (gameData.roundStatus !== "open") {
      setStatusMsg("La ronda no está abierta para ofertar.");
      return;
    }
    const amountNum = Number(bidAmount);
    if (!amountNum || amountNum <= 0) {
      setStatusMsg("Pon una cantidad válida.");
      return;
    }
    if (amountNum > (playerData.money || 0)) {
      setStatusMsg("No puedes ofertar más de lo que tienes.");
      return;
    }

    const roundIndex = gameData.currentRound || 1;
    const bidRef = doc(
      db,
      "games",
      GAME_ID,
      "rounds",
      String(roundIndex),
      "bids",
      playerData.id
    );
    await setDoc(bidRef, {
      playerId: playerData.id,
      playerName: playerData.name,
      amount: amountNum,
      updatedAt: new Date(),
    });
    await addLog("bid_placed", `${playerData.name} ofertó por el pan`, {
      playerId: playerData.id,
      playerName: playerData.name,
      bidAmount: amountNum,
      round: roundIndex,
      playerMoney: playerData.money,
    });
    setStatusMsg("Oferta registrada.");
  };

  // Votar en encuesta
  const handlePollVote = async (vote) => {
    if (!playerData || !gameData) return;
    
    // Verificar que el jugador esté activo
    if (playerData.active === false) {
      setStatusMsg("Los jugadores eliminados no pueden votar.");
      return;
    }
    
    if (gameData.roundStatus !== "open") {
      setStatusMsg("No hay encuesta activa ahora mismo.");
      return;
    }
    setIsSubmittingVote(true);
    try {
      const roundIndex = gameData.currentRound || 1;
      const voteRef = doc(
        db,
        "games",
        GAME_ID,
        "rounds",
        String(roundIndex),
        "pollVotes",
        playerData.id
      );
      await setDoc(voteRef, {
        playerId: playerData.id,
        playerName: playerData.name,
        vote,
        createdAt: new Date(),
      });
      await addLog("poll_vote", `${playerData.name} votó en la encuesta`, {
        playerId: playerData.id,
        playerName: playerData.name,
        vote: vote,
        round: roundIndex,
      });
      setPollVote(vote);
      setStatusMsg("Voto registrado.");
      
      // Verificar si todos los jugadores ACTIVOS han votado y procesar inmediatamente
      const votesCol = collection(db, "games", GAME_ID, "rounds", String(roundIndex), "pollVotes");
      const votesSnap = await getDocs(votesCol);
      const playersCol = collection(db, "games", GAME_ID, "players");
      const playersSnap = await getDocs(playersCol);
      
      // Contar solo jugadores activos
      let activePlayersCount = 0;
      playersSnap.forEach((doc) => {
        const data = doc.data();
        if (data.active !== false) {
          activePlayersCount++;
        }
      });
      
      // Si todos los jugadores activos han votado, procesar la encuesta inmediatamente
      if (votesSnap.size === activePlayersCount && activePlayersCount > 0) {
        console.log(`Todos los ${activePlayersCount} jugadores activos han votado, procesando encuesta inmediatamente...`);
        await processPollVotes(roundIndex);
      }
    } finally {
      setIsSubmittingVote(false);
    }
  };

  const canPlay = !!playerData && !!gameData && gameData.roundStatus === "open";

  return {
    playerId,
    playerName,
    setPlayerName,
    playerData,
    gameData,
    topBids,
    bidAmount,
    setBidAmount,
    statusMsg,
    pollVote,
    isSubmittingVote,
    timeLeft,
    canPlay,
    handleJoin,
    awardMoney,
    handleBid,
    handlePollVote,
    totalPlayers,
    totalVotes,
    isProcessingRound,
    allPlayers,
    isSpectator,
    isShowingResume,
  };
}
